import { useEffect, useState } from "react";
import { DateTime } from "luxon";

import { ILead, LeadSource, QuoteType } from "../../../../models/Lead";
import { ISavCustomerName } from "../../../../data/useSav";
import PriceFormat from "../../../../utils/priceFormat";
import formatDateTime from './../../../../utils/formatDateTime';
import SendQualifierButton from "../../SendQualifierButton";
import { GetQuoteType } from "../../LeadInformation/QuoteCalculationCard";
import SavCustomerPickerPill from "../SavCustomerPickerPill";
import { useAuth } from "../../../../contexts/AuthContext";
import EnginesWorldwideLookup from "../../../vehicles/EnginesWorldwideLookup";

export default function LeadsTableSubComponent({
  lead,
  isSavCustomer = false,
  savCustomers = [],
  onAllocateLead,
  allocatingLeadId = null,
}: {
  lead: ILead;
  isSavCustomer?: boolean;
  savCustomers?: ISavCustomerName[];
  onAllocateLead?: (leadId: string, targetCustomerId: string) => void;
  allocatingLeadId?: string | null;
}) {
  const { customerId } = useAuth();
  const [targetCustomerId, setTargetCustomerId] = useState<string>("");
  const allocatedCustomerName = savCustomers.find(x => x.id === lead.allocatedToCustomerId)?.name;
  const isAllocated = !!lead.allocatedToCustomerId;

  useEffect(() => {
    if (!isSavCustomer || !savCustomers.length || targetCustomerId) return;
    const defaultCustomerId = savCustomers.some(x => x.id === customerId)
      ? customerId
      : savCustomers[0].id;
    setTargetCustomerId(defaultCustomerId ?? "");
  }, [isSavCustomer, savCustomers, targetCustomerId, customerId]);


  return (
    <>
      <div className="flex justify-between bg-gray-100 pl-28 p-4">
        <div className="flex gap-x-10">
          <div className="flex flex-col">
            <span className="font-bold">Price</span>
            <span>{PriceFormat(lead.price)}</span>
          </div>

          {lead.applyVAT && (
            <div className="flex flex-col">
              <span className="font-bold">With VAT</span>
              <span>{PriceFormat(lead.priceWithVAT)}</span>
            </div>
          )}

          <div className="flex flex-col">
            <span className="font-bold">Website</span>
            <span>{LeadSource[lead.website]}</span>
          </div>

          {(lead.website == LeadSource.SellAVehicle || lead?.website == LeadSource.Widget) && (
            <div className="flex flex-col">
              <span className="font-bold">Quote Type</span>
              <span>{GetQuoteType(lead.quoteType)}</span>
            </div>
          )}

          <div className="flex flex-col">
            <span className="font-bold">Exported</span>
            <span className="flex justify-center">{lead.exportedOn !== null ? "✔" : "X"}</span>
          </div>

          <div className="flex flex-col">
            <span className="font-bold">Created</span>
            <span className="flex justify-center">{formatDateTime(lead.created, DateTime.DATE_SHORT)}</span>
          </div>

          {lead.vehicle && (
            <div className="flex flex-col">
              <span className="font-bold">EWW Prices</span>
              <EnginesWorldwideLookup vehicle={lead.vehicle} variant="inline" />
            </div>
          )}

          <div className="flex flex-col">
            <span className="font-bold">VLA Engine Price</span>
            <span>
              {lead.vehicle?.vlaEngineOffer ?
                PriceFormat(parseFloat(lead.vehicle?.vlaEngineOffer))
                : "N/A"
              }
            </span>
          </div>

          <div className="flex flex-col">
            <span className="font-bold">VLA VFE Price</span>
            <span>
              {lead.vehicle?.vlaVfeOffer ?
                PriceFormat(parseFloat(lead.vehicle?.vlaVfeOffer))
                : "N/A"
              }
            </span>
          </div>

          <div className="flex flex-col">
            <span className="font-bold">Average Catalytic Price</span>
            <span>
              {lead.vehicle?.enginesWorldwideVfeOffer ?
                PriceFormat(parseFloat(lead.catalyticAveragePrice?.toString()!))
                : lead.vehicle?.catalyticResults && lead.vehicle.catalyticResults.length > 0 ?
                  PriceFormat(parseFloat((lead.vehicle.catalyticResults.reduce((sum, x) => sum + Number(x.value), 0) /
                    lead.vehicle.catalyticResults.length).toString()))
                  : "N/A"
              }
            </span>
          </div>
        </div>

        <div className="flex items-center">
          <div className="flex flex-col items-end gap-2">
            <SendQualifierButton lead={lead} />

            {isSavCustomer && !isAllocated && (
              <div className="flex items-center gap-2">
                <SavCustomerPickerPill
                  value={targetCustomerId || null}
                  customers={savCustomers ?? []}
                  onChange={(id) => setTargetCustomerId(id ?? "")}
                  placeholder="Assign customer"
                />

                <button
                  className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
                  disabled={!targetCustomerId || allocatingLeadId === lead.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAllocateLead?.(lead.id, targetCustomerId);
                  }}
                >
                  {allocatingLeadId === lead.id ? "Allocating..." : "Allocate"}
                </button>
              </div>
            )}

            {isSavCustomer && isAllocated && (
              <div className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-semibold">
                Allocated to {allocatedCustomerName || "-"}
              </div>
            )}
          </div>
        </div>

      </div>
    </>
  )
}